import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { GitCompare, AlertCircle, Users, DollarSign, Percent } from 'lucide-react';
import { useProductBundles, calculateBundleSavings } from '@/hooks/useProductBundles';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CannibalizationPair {
  bundle_a_id: string;
  bundle_a_name: string;
  bundle_b_id: string;
  bundle_b_name: string;
  overlap_score: number;
  shared_accounts: number;
  price_similarity: number;
  product_overlap: number;
  recommendation: string;
  risk_level: 'low' | 'medium' | 'high';
}

export function BundleCannibalizationAnalysis() {
  const { data: bundles } = useProductBundles();
  const { data: conversionStats } = useBundleConversionStats();

  // Fetch deal data for account overlap analysis
  const { data: dealBundleData } = useQuery({
    queryKey: ['deal-bundle-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          deal:deals(account_id)
        `)
        .not('bundle_id', 'is', null);

      if (error) throw error;

      // Group accounts by bundle
      const bundleAccounts: Record<string, Set<string>> = {};
      data?.forEach((item: any) => {
        if (item.bundle_id && item.deal?.account_id) {
          if (!bundleAccounts[item.bundle_id]) {
            bundleAccounts[item.bundle_id] = new Set();
          }
          bundleAccounts[item.bundle_id].add(item.deal.account_id);
        }
      });

      return bundleAccounts;
    },
  });

  const cannibalizationPairs = useMemo(() => {
    if (!bundles || bundles.length < 2 || !dealBundleData) return [];

    const pairs: CannibalizationPair[] = [];
    const activeBundles = bundles.filter(b => b.is_active);

    for (let i = 0; i < activeBundles.length; i++) {
      for (let j = i + 1; j < activeBundles.length; j++) {
        const bundleA = activeBundles[i];
        const bundleB = activeBundles[j];

        // Calculate product overlap
        const productsA = new Set(bundleA.items?.map(item => item.product_id) || []);
        const productsB = new Set(bundleB.items?.map(item => item.product_id) || []);
        const sharedProducts = [...productsA].filter(p => productsB.has(p)).length;
        const totalUniqueProducts = new Set([...productsA, ...productsB]).size;
        const productOverlap = totalUniqueProducts > 0 
          ? (sharedProducts / totalUniqueProducts) * 100 
          : 0;

        // Calculate price similarity (within 30% of each other)
        const priceA = bundleA.bundle_price;
        const priceB = bundleB.bundle_price;
        const priceDiff = Math.abs(priceA - priceB);
        const avgPrice = (priceA + priceB) / 2;
        const priceSimilarity = avgPrice > 0 
          ? Math.max(0, 100 - (priceDiff / avgPrice) * 100) 
          : 0;

        // Calculate account overlap
        const accountsA = dealBundleData[bundleA.id] || new Set();
        const accountsB = dealBundleData[bundleB.id] || new Set();
        const sharedAccounts = [...accountsA].filter(a => accountsB.has(a)).length;

        // Calculate overall overlap score (weighted)
        const overlapScore = (productOverlap * 0.5) + (priceSimilarity * 0.3) + 
          (sharedAccounts > 0 ? Math.min(sharedAccounts * 10, 20) : 0);

        // Only include pairs with meaningful overlap
        if (overlapScore > 30 || productOverlap > 40 || sharedAccounts > 0) {
          let riskLevel: 'low' | 'medium' | 'high' = 'low';
          let recommendation = '';

          if (overlapScore > 70 || (productOverlap > 60 && priceSimilarity > 70)) {
            riskLevel = 'high';
            recommendation = 'Consider merging or significantly differentiating these bundles';
          } else if (overlapScore > 50 || productOverlap > 50) {
            riskLevel = 'medium';
            recommendation = 'Review pricing strategy to avoid direct competition';
          } else {
            riskLevel = 'low';
            recommendation = 'Minor overlap - monitor for changes';
          }

          pairs.push({
            bundle_a_id: bundleA.id,
            bundle_a_name: bundleA.name,
            bundle_b_id: bundleB.id,
            bundle_b_name: bundleB.name,
            overlap_score: Math.round(overlapScore),
            shared_accounts: sharedAccounts,
            price_similarity: Math.round(priceSimilarity),
            product_overlap: Math.round(productOverlap),
            recommendation,
            risk_level: riskLevel,
          });
        }
      }
    }

    return pairs.sort((a, b) => b.overlap_score - a.overlap_score);
  }, [bundles, dealBundleData]);

  const highRiskCount = cannibalizationPairs.filter(p => p.risk_level === 'high').length;
  const mediumRiskCount = cannibalizationPairs.filter(p => p.risk_level === 'medium').length;

  const getRiskColor = (risk: CannibalizationPair['risk_level']) => {
    switch (risk) {
      case 'high': return 'bg-destructive text-destructive-foreground';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
    }
  };

  const getProgressColor = (value: number) => {
    if (value > 70) return 'bg-destructive';
    if (value > 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-primary" />
            <CardTitle>Bundle Cannibalization Analysis</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {highRiskCount > 0 && (
              <Badge variant="destructive">{highRiskCount} High Risk</Badge>
            )}
            {mediumRiskCount > 0 && (
              <Badge className="bg-yellow-500">{mediumRiskCount} Medium</Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Identify bundles that compete with each other for the same deals and customers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {cannibalizationPairs.length > 0 ? (
          <div className="space-y-4">
            {cannibalizationPairs.map((pair, index) => (
              <div key={`${pair.bundle_a_id}-${pair.bundle_b_id}`} className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{pair.bundle_a_name}</span>
                    <GitCompare className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">{pair.bundle_b_name}</span>
                  </div>
                  <Badge className={getRiskColor(pair.risk_level)}>
                    {pair.risk_level} risk
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Percent className="h-3 w-3" />
                      Product Overlap
                    </div>
                    <Progress value={pair.product_overlap} className={`h-2 ${getProgressColor(pair.product_overlap)}`} />
                    <span className="text-sm font-medium">{pair.product_overlap}%</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      Price Similarity
                    </div>
                    <Progress value={pair.price_similarity} className={`h-2 ${getProgressColor(pair.price_similarity)}`} />
                    <span className="text-sm font-medium">{pair.price_similarity}%</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      Shared Accounts
                    </div>
                    <div className="text-sm font-medium pt-2">{pair.shared_accounts} accounts</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm p-2 rounded bg-muted/50">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{pair.recommendation}</span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Overall Overlap Score:</span>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div 
                      className={`h-full ${getProgressColor(pair.overlap_score)}`}
                      style={{ width: `${pair.overlap_score}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold">{pair.overlap_score}%</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <GitCompare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No significant cannibalization detected</p>
            <p className="text-sm">Your bundles are well-differentiated</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
